import type { Options as DeepmergeOptions } from 'deepmerge'
import type { LocationQueryValue } from 'vue-router'
import type { WrappedRequest } from '@/types/curRequest.d'
import type { MutationVars } from '@/types/request'
import { useMutation, useQuery } from '@tanstack/vue-query'
import deepmerge from 'deepmerge'
import { cloneDeep, debounce, get, has, set, unset } from 'lodash-es'
import { computed, nextTick, reactive, ref, unref, watch } from 'vue'

import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { cleanQueryKey, CRUD_MUTATION_PREFIX } from '@/composables/query'
import { invalidateV3ExpertAssessment } from '@/composables/query/assessment_invalidation'
import enums from '@/plugins/enums'
import utils from '@/plugins/utils'
import store from '@/store'
import { Actions } from '@/store/Log/index.js'

import { queryClient, ReApi, ReApiVersion, ReEndpoint } from './query'
import wrapRequest from './requestWrapper.js'
import { reactiveAny } from './utils/reactive.js'

const { requestStatus: status, buildingTypes } = enums

// Types
interface UseRequestOptions {
  requestRefInit?: string | null
  startDisabled?: boolean
  flowType?: string
}

interface CreateRequestParams {
  customer_ref?: string | null
  ignore_conflicts?: boolean
  requested_valuation_type?: string | LocationQueryValue
}

interface CreateRequestResponse {
  valuation_request_ref: string
}

interface PatchOptions {
  prefix?: string
  extraPatchData?: Record<string, unknown>
  debounce?: boolean
  optimistic?: boolean
  forceRefetch?: boolean
  mergeOptions?: DeepmergeOptions
  overwriteKey?: string
  exclusiveKeys?: string[]
  combineArrays?: boolean
  readOnly?: boolean
  reactive?: boolean
  mutationCallbackFn?: (patchedData: unknown, data: unknown) => void | Promise<void>
}

interface GetReactiveOptions {
  initOnly?: boolean
  precision?: number
  placeholder?: unknown
  defaultValue?: unknown
  mustExist?: boolean
  notifyOnPatch?: boolean
  throttle?: boolean
  reactiveObject?: boolean
  combineArrays?: boolean
  debounce?: boolean
  prefix?: string
  readOnly?: boolean
  overwriteKey?: string
  exclusiveKeys?: string[]
  forceRefetch?: boolean
  mutationCallbackFn?: (patchedData: unknown, data: unknown) => void | Promise<void>
  onSuccess?: (data: unknown, options: MutationVars) => void
}

export function useRequest({ requestRefInit, startDisabled, flowType }: UseRequestOptions = {}) {
  // const route = useRoute()
  // const { hasRole } = useUser()
  // rootGetters['auth/hasRole'] = hasRole

  // TODO: deal with unread_messages
  // TODO: fetch documents on request update?
  // TODO: deal with fetchStreetviewInfo

  // TODO: decide if we want to allow updating requestRef (or force a new composable instance)
  // const requestRef = ref(requestRefInit)

  // const queryClient = useQueryClient()

  // Easier to grab the route directly here:
  const route = useRoute()
  const router = useRouter()

  const { t } = useI18n()

  const internalRef = ref(requestRefInit || null)
  const requestRef = computed(() => unref(internalRef) || (route.query.valuation_request_ref as string))
  const updateRequestRef = (value: string | null) => {
    if (internalRef.value !== value) {
      internalRef.value = value
    }
  }
  watch(
    () => route.query.valuation_request_ref,
    () => updateRequestRef(null),
  )
  watch(
    () => route.query.resetCurRequest,
    (value) => {
      if (value) {
        updateRequestRef(null)
        nextTick(() => {
          router.replace({
            query: { ...route.query, resetCurRequest: undefined },
          })
        })
      }
    },
  )

  const disable = ref(!!startDisabled)

  const isEnabled = computed(() => !disable.value && !!requestRef.value)

  const queryKey = computed(() =>
    flowType
      ? [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, requestRef, flowType]
      : [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, requestRef],
  )

  // NOTE: when called with an empty initial requestRef, this will create an inactive invalid query
  // which will fail when invalidating and not filtering for active…
  // TODO: avoid that?
  const query = useQuery<WrappedRequest | null, Error>({
    queryKey,
    // TODO: check that the wrapper gets run when the cache gets updated directly…
    select(data): WrappedRequest | null {
      if (data) {
        if (data.valuation_request) {
          return wrapRequest(data.valuation_request)
        }
        return wrapRequest(data)
      }
      return data
    },
    placeholderData: null,
    enabled: isEnabled,
    // DEBUG: artificially delay the query to test loading state:
    // meta: { throttle: 2000 },
    throwOnError: (error: Error): boolean => {
      store.dispatch(Actions.LOG_ERROR, {
        error: {
          // TODO: add clean error i18n for this code:
          error_code: 'cant_load_request',
          debug: { message: error.message, stack: error.stack },
        },
        errorType: 'error',
      })
      return false
    },
  })

  const mutation = useMutation<unknown, Error, MutationVars>({
    // NOTE: the PATCH request endpoint does not follow CRUD conventions (does not return the updated item)
    mutationKey: queryKey.value,
  })

  // NOTE: we use a separate mutation for status/type/etc updates that should be online only
  // this mutation omits the OFFLINE_MUTATION_PREFIX
  const requestMutation = useMutation<unknown, Error, MutationVars>({
    onSuccess: async () => {
      // Refresh individual request (will update list cache thanks to watch on query.data):
      disable.value = false
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: queryKey.value,
          type: 'active',
          exact: true,
        }),
        requestRef.value
          ? invalidateV3ExpertAssessment(requestRef.value)
          : Promise.resolve(),
      ])
    },
  })

  const createQueryKeyBase = [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST]

  // TODO: merge with requestMutation (need to handle different onSuccess)
  const createMutation = useMutation<CreateRequestResponse, Error, MutationVars>({
    mutationKey: [CRUD_MUTATION_PREFIX, ...createQueryKeyBase],
  })

  const createRequest = (
    params: CreateRequestParams = {
      customer_ref: null,
      ignore_conflicts: true,
      requested_valuation_type: 'avm',
    },
  ): Promise<CreateRequestResponse> => {
    // const { customer_ref, ignore_conflicts, requested_valuation_type } = payload

    // TODO: handle conflict error here and set a conflictingVals ref that can be used by calling code.
    return new Promise((resolve, reject) => {
      createMutation
        .mutateAsync({
          verb: 'POST',
          queryKey: [...createQueryKeyBase, params],
          axiosOptions: {
            // TODO: 05/03/2026 Remove 403 after deployed BE is no longer on frozen tag
            validateStatus: (status: number) => status < 400 || status === 409 || status === 403,
          },
        })
        .then((response) => {
          // NOTE: main request list is auto-refreshed, we reload user-requests manually:
          // TODO: remove when we retire user request endpoint
          queryClient.invalidateQueries({
            queryKey: [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.USER, 'request'],
          })

          // @ts-expect-error Idk
          if (response.errors && !params.ignore_conflicts) {
            resolve(response)
            return
          }

          if (response?.valuation_request_ref) {
            updateRequestRef(response.valuation_request_ref)
          }

          onLoad((data) => {
            resolve({ ...response, ...data })
          })
        })
        .catch(error => reject(error))
    })
  }

  // TODO: move debouncing to query level?
  const DEBOUNCE_TIME = 1000
  const patchData = ref<Record<string, unknown>>({})
  const debouncedPatch = debounce((options: PatchOptions = {}) => {
    // create copy avoid issues with patchData being reset before mutationCallbackFn runs
    const patchedData = cloneDeep(patchData.value)
    const promise = mutation.mutateAsync(
      {
        verb: 'PATCH',
        queryKey: queryKey.value,
        data: patchedData,
        ...options,
      },
      {
        onSuccess: async (data: unknown) => {
          if (typeof options.mutationCallbackFn === 'function') {
            await options.mutationCallbackFn(patchedData, data)
          }
          if (requestRef.value)
            await invalidateV3ExpertAssessment(requestRef.value)
        },
        onSettled: () => {
          isPatching.value = false
        },
      },
    )
    patchData.value = {}
    return promise
  }, DEBOUNCE_TIME)

  // Options:
  //      prefix: prepend with a path (eg dvm_features., ovm_features., etc)
  //      extraPatchData: additional data to be patched when setting the value
  //      debounce: use debounced patching (default false)
  //      optimistic: 'optimistically update the cache (default true)',
  //      forceRefetch: force a refetch after patching (default false)
  //      mergeOptions: options passed to deepmerge (allows finer merge strategy)
  //      overwriteKey: key to overwrite rather than merge (useful for arrays)

  const isPatching = ref(false)

  const applyExclusiveKeys = function (
    queuedData: Record<string, unknown>,
    incomingData: Record<string, unknown>,
    exclusiveKeys?: string[],
  ) {
    if (!exclusiveKeys?.length)
      return queuedData

    exclusiveKeys.forEach((incomingPath) => {
      if (!has(incomingData, incomingPath))
        return

      exclusiveKeys.forEach((exclusivePath) => {
        if (exclusivePath !== incomingPath) {
          unset(queuedData, exclusivePath)
        }
      })
    })

    return queuedData
  }

  const patch = function (data: Record<string, unknown>, options: PatchOptions = {}) {
    if (!requestRef.value) {
      console.warn('Trying to patch a blank request:', data, options)
      return Promise.reject(new Error('Cannot patch a blank request'))
    }
    if (options.readOnly) {
      console.warn('Trying to patch a readOnly field', data, options)
      return Promise.reject(new Error('Cannot patch a readOnly field'))
    }

    const isPatchingOtherData = Object.keys(data)[0] === 'other_data'
    if (!can.edit && !isPatchingOtherData) {
      console.debug('Trying to patch a request that is no longer editable:', data, options)
      return Promise.reject(new Error('Cannot patch a request that is no longer editable'))
    }

    isPatching.value = true

    options.mergeOptions = options.mergeOptions || {}

    if (options.combineArrays) {
      // When merging arrays, combine same indexes, rather than append:
      options.mergeOptions.arrayMerge = (
        target: unknown[],
        source: unknown[],

        opts: any,
      ): unknown[] => {
        const destination = target.slice()
        source.forEach((item, index) => {
          if (typeof destination[index] === 'undefined') {
            destination[index] = opts.cloneUnlessOtherwiseSpecified(item, opts)
          }
          else if (opts.isMergeableObject(item)) {
            destination[index] = deepmerge(target[index] as object, item as object, opts)
          }
          else if (!target.includes(item)) {
            destination.push(item)
          }
        })
        return destination
      }
    }
    else {
      // Default to replacing array:
      options.mergeOptions.arrayMerge = (_target: unknown[], source: unknown[]): unknown[] => source
    }

    if (options.overwriteKey) {
      // when handling 'props.feature': overwrite rather than merge:
      // eg: { foo: [1, 2] } + { foo: [3] } -> { foo: [3] }
      options.mergeOptions.customMerge = (key: string) => {
        if (key == options.overwriteKey)
          return (_: unknown, b: unknown) => b
      }
    }

    if (options.extraPatchData)
      data = deepmerge(data, options.extraPatchData)

    // if patching an array, the backend will overwrite existing array,
    // if combineArrays = true, we first merge with existing cache data
    //  using either prefix or the first key as base to patch over
    const combineKey = options.prefix ?? Object.keys(data)[0]
    if (
      combineKey
      && options.combineArrays
      // Do not add to patchData if the key is already set in patchData:
      && (!options.debounce || get(patchData.value, combineKey) === undefined)
    ) {
      const oldData = query.data.value
      if (oldData) {
        // Extract array containing only the data to be patched:
        const old = get(oldData, combineKey)
        if (old) {
          data = deepmerge(set({}, combineKey, old), data, options.mergeOptions)
        }
      }
      else {
        console.warn('Patching an array without existing data. This might lead to losing data.')
      }
    }

    if (options.debounce) {
      patchData.value = applyExclusiveKeys(cloneDeep(patchData.value), data, options.exclusiveKeys)
      patchData.value = deepmerge(patchData.value, data, options.mergeOptions)
      const cleanedQueryKey = cleanQueryKey(queryKey.value)
      // Cancel any active queries for this queryKey (so they don't overwrite the optimistic update):
      queryClient.cancelQueries({ queryKey: cleanedQueryKey, type: 'active', exact: true })
      if (options.reactive ?? options.debounce) {
        // Optimistically apply the change (before the debounce):
        queryClient.setQueryData(cleanedQueryKey, (old: Record<string, unknown> = {}) => {
          return deepmerge(old, data, options.mergeOptions)
        })
      }
      debouncedPatch(options)
      return Promise.resolve()
    }
    else {
      return mutation.mutateAsync(
        {
          verb: 'PATCH',
          queryKey: queryKey.value,
          data,
          ...options,
        },
        {
          onSuccess: async (responseData: unknown) => {
            if (typeof options.mutationCallbackFn === 'function') {
              await options.mutationCallbackFn(responseData, query.data.value)
            }
            if (requestRef.value)
              await invalidateV3ExpertAssessment(requestRef.value)
          },
          onSettled: () => {
            isPatching.value = false
          },
        },
      )
    }
  }

  const putUpdate = function (key: string, data: unknown) {
    // FIXME: should fail (noisily) if the requestRef is not set:
    return requestMutation.mutateAsync({
      verb: 'PUT',
      queryKey: [...queryKey.value, key, data],
    })
  }

  const patchUpdate = async function (key: string, data: Record<string, unknown>, cb?: () => void) {
    // FIXME: should fail (noisily) if the requestRef is not set:
    const res = await requestMutation.mutateAsync({
      verb: 'PATCH',
      queryKey: [...queryKey.value, key],
      data,
    })

    if (cb)
      cb()

    return res
  }

  const updateBorrower = (data: unknown) => putUpdate('borrower', data)
  const updateValuer = (data: unknown) => putUpdate('valuer', data)
  const updateOwner = () => putUpdate('owner', {})
  const updateRequestedType = (reqType: string) => putUpdate('requested_type', { type: reqType })
  const updateStatus = (action: string, data?: Record<string, unknown>) =>
    putUpdate('status', { action, ...data })
  const patchPricing = (data: Record<string, unknown>, cb: () => void = () => {}) =>
    patchUpdate('pricing', data, cb)

  const isRecomputingAvm = ref(false)
  const recomputeAvm = async () => {
    isRecomputingAvm.value = true
    await updateStatus(enums.requestAction.RECOMPUTE_AVM)
    isRecomputingAvm.value = false
  }

  // Internal function used to set a value in the reactive object
  function _setVal(key: string, value: unknown, options: GetReactiveOptions) {
    if (options.precision)
      value = Math.round((value as number) * 10 ** options.precision) / 10 ** options.precision

    if (value === undefined) {
      console.warn('Trying to patch with undefined value. Casting to null.', key, value)
      value = null
    }

    // Don't trigger a patch if value unchanged:
    if (get(query.data.value, key) === value)
      return

    patch(set({}, key, value), {
      debounce: true,
      ...options,
    })
  }

  const optionDoc: Record<string, string> = {
    prefix: 'prepend with a path (eg dvm_features, ovm_features, etc) (do not include final `.`)',
    initOnly: 'send back copy of the value once loaded (useful for initial values)',
    precision: 'round to a given precision',
    readOnly: 'prevent setting the value',
    placeholder: 'if value is undefined, return this value (does not update backend)',
    defaultValue: 'if value is undefined, will update backend and return defaultValue',
    mustExist: 'will throw an error if set to true and trying to reach an inexistant key (default false)',
    notifyOnPatch: 'notify the user that the value have been saved by displaying a toast notification',
    // Patch options:
    extraPatchData: 'additional data to be patched when setting the value',
    debounce: 'use debounced patching (default true)',
    mergeOptions: 'options passed to deepmerge (allows finer merge strategy)',
    reactive: 'when debouncing: apply change optimistically (default to value of debounce)',
    optimistic: 'optimistically update the cache (default true)',
    forceRefetch: 'force a refetch after patching (default true)',
    combineArrays: 'when merging arrays, combine same indexes, rather than append',
    overwriteKey: 'key to overwrite rather than merge (useful for arrays)',
    exclusiveKeys: 'full paths that are mutually exclusive inside the debounced patch queue (last write wins)',
    reactiveObject: 'return an object containing reactive values',
    mutationCallbackFn: 'function to call after a Patch returns',
    throttle: 'throttle the patch requests (default false)',
  }

  const getReactive = function (field: string, options: GetReactiveOptions = {}) {
    Object.keys(options).forEach((key) => {
      if (!optionDoc[key]) {
        console.warn(`Unknown option for ${field}: ${key}`)
      }
    })

    if (options.notifyOnPatch) {
      // Store notification details here, to avoid dealing with i18n in the query composable
      // TODO: could we maybe pass a key to the notification?
      options.onSuccess = (_data: unknown, { verb }: MutationVars) => {
        if (verb === 'PATCH') {
          store.dispatch(Actions.NOTIFY_USER, {
            message: t('monolith.shared.notification.save_success'),
            color: 'positive',
          })
        }
      }
    }

    if (options.prefix) {
      field = `${options.prefix}.${field}`
    }

    if (options.initOnly) {
      const val = query.data.value
        ? get(query.data.value, field, options.defaultValue ?? options.placeholder)
        : options.placeholder
      const valRef = ref(cloneDeep(val ?? options.placeholder))

      // NOTE: only watch if the query has not already been set:
      if (!query.data.value) {
        const unwatch = watch(query.data, (newVal) => {
          valRef.value = cloneDeep(newVal)
          if (options.initOnly && query.data?.value) {
            unwatch()
          }
        })
      }

      return valRef
    }

    return computed({
      get() {
        if (!query.data || !query.data.value)
          return undefined

        if (options.mustExist === true && !has(query.data.value, field))
          throw new Error(`Error getting reactive value: ${field}`)

        let val = get(query.data.value, field)

        if (val && options.precision)
          val = Math.round(val * 10 ** options.precision) / 10 ** options.precision

        // If value is undefined, return placeholder or defaultValue:
        if (val === undefined) {
          val = options.defaultValue ?? options.placeholder
          // When defaultValue is set, trigger a backend patch (if editable), but do not debounce:
          if (options.defaultValue !== undefined && can.edit)
            _setVal(field, val, { ...options, debounce: false })
        }

        // Wrap object in reactive proxy to allow setting nested values:
        if (typeof val === 'object' && val !== null) {
          val = new Proxy(val as object, {
            set(_target: object, key: string | symbol, value: unknown): boolean {
              _setVal(`${field}.${String(key)}`, value, options)
              return true
            },
          })
        }

        return val
      },
      set(val) {
        _setVal(field, val, options)
      },
    })
  }

  // Check options doc on getReactive
  const mapReactives = (fields: string[], options: GetReactiveOptions = {}) => {
    return utils.objectFromArray(fields, (f: string) => [
      f.split('.').at(-1)!.replace('[0]', ''),
      getReactive(f, {
        // If prefixed key contains [0], automatically combine arrays:
        // TODO: only combine key, not all arrays
        combineArrays: f.includes('[0]') || options.prefix?.includes('[0]'),
        ...options,
      }),
    ])
  }
  const mapReadOnly = (fields: string[], options: GetReactiveOptions = {}) => {
    return mapReactives(fields, { ...options, readOnly: true })
  }
  const mapReactiveNumbers = (fields: string[], options: GetReactiveOptions = {}) => {
    return mapReactives(fields, { ...options, precision: 2 })
  }

  const initValues = (fields: string[], options: GetReactiveOptions = {}) =>
    mapReactives(fields, { initOnly: true, ...options })

  // const loadReactives = (fields) => {}

  // Helper computed refs to avoid circular reference in `is` reactive
  const isSubmittingBorrower = computed(() => !!query.data?.value?.is_borrower_submitted)

  // TODO: check if we need both this reactive and top-level?
  const is = reactive({
    // Building type:
    // TODO: do these utils (is_house etc) really need to be defined outside of this composable?
    house: computed(() => utils.is_house(query.data?.value?.features?.f_building_type)),
    apartment: computed(() => utils.is_apartment(query.data?.value?.features?.f_building_type)),
    plot: computed(() => utils.is_plot(query.data?.value?.features?.f_building_type)),
    new: computed(() => utils.is_new(query.data?.value?.features?.f_building_type)),
    garage: computed(() => query.data?.value?.features?.f_building_type === enums.buildingTypes.GARAGE),
    commercial: computed(
      () => query.data?.value?.features?.f_building_type === enums.buildingTypes.COMMERCIAL,
    ),
    other: computed(() => query.data?.value?.features?.f_building_type === enums.buildingTypes.OTHER),
    apartmentBlock: computed(() =>
      // NOTE: INVESTMENT is deprecated, but we keep it here for backward compatibility
      // TODO: [22 sept 2025] remove INVESTMENT in next major release
      [enums.buildingTypes.INVESTMENT, enums.buildingTypes.APARTMENT_BLOCK].includes(
        query.data?.value?.features?.f_building_type ?? '',
      ),
    ),
    existing: computed(() => utils.is_existing(query.data?.value?.features?.f_building_type)),
    nonResidential: computed(() =>
      [
        buildingTypes.GARAGE,
        buildingTypes.COMMERCIAL,
        buildingTypes.INVESTMENT,
        buildingTypes.APARTMENT_BLOCK,
      ].includes(query.data?.value?.features?.f_building_type ?? ''),
    ),
    building: computed(() => !utils.is_plot(query.data?.value?.features?.f_building_type)),
    // Status:
    draft: computed(() => query.data?.value?.status === status.DRAFT),
    valued: computed(() => query.data?.value?.status === status.VALUED),
    submitted: computed(() => query.data?.value?.status === status.SUBMITTED),
    cancelled: computed(() => query.data?.value?.status === status.CANCELLED),
    failed: computed(() => query.data?.value?.status === status.FAILED),
    // Type:
    ovm: computed(() => query.data?.value?.valuation_type === 'ovm'),
    // TODO: should we also check that val type is ovm?
    ovm_whitelabel: computed(() => !!query.data?.value?.is_ovm_whitelabel),
    dvm_whitelabel: computed(() => !!query.data?.value?.is_dvm_whitelabel),
    whitelabel: computed(
      () => !!query.data?.value?.is_ovm_whitelabel || !!query.data?.value?.is_dvm_whitelabel,
    ),
    avm: computed(() => query.data?.value?.valuation_type === 'avm'),
    dvm: computed(() => query.data?.value?.valuation_type === 'dvm'),
    oepc: computed(() => query.data?.value?.valuation_type === 'oepc'),
    submittingBorrower: isSubmittingBorrower,
    selfSubmittingBorrower: computed(
      () => !!isSubmittingBorrower.value && !!query.data?.value?.borrower?.is_self,
    ),
    referringOwner: computed(() => isSubmittingBorrower.value && !!query.data?.value?.owner?.is_self),
    refinancing: computed(() => query.data?.value?.purpose === enums.requestPurpose.REFINANCING),
  })

  const canSubmitDvm = computed(() => !!query.data?.value?.can.submit.dvm)
  const canSubmitOvm = computed(() => !!query.data?.value?.can.submit.ovm)
  const canSubmitReasons = computed(() => !!query.data?.value?.can.reasons)
  const canUnsubmitValuation = computed(() => !!query.data?.value?.can.unsubmit_valuation)
  const canSubmitValuation = computed(() => !!query.data?.value?.can.submit_valuation)
  const canDeferToOnsite = computed(() => !!query.data?.value?.can.defer_to_onsite)
  const canAcceptValuation = computed(() => !!query.data?.value?.can.accept_valuation)

  const can = reactive({
    edit: computed(() => !!query.data?.value?.can?.edit),
    submitDvm: canSubmitDvm,
    submitOvm: canSubmitOvm,
    submit: computed(() => canSubmitOvm.value || canSubmitDvm.value),
    submit_reasons: canSubmitReasons,
    unsubmitValuation: canUnsubmitValuation,
    submitValuation: canSubmitValuation,
    deferToOnsite: canDeferToOnsite,
    acceptValuation: canAcceptValuation,
  })

  const onLoad = function (callback: (data?: WrappedRequest | null) => void) {
    // TODO: should we still run callback if the query is not enabled?
    if (query.isFetched.value) {
      callback(query.data.value)
    }
    else {
      const unwatch = watch(query.isFetched, () => {
        if (query.isFetched.value) {
          callback(query.data.value)
          unwatch()
        }
      })
    }
  }

  const onPatch = function (callback: (data?: WrappedRequest | null) => void) {
    if (!isPatching.value) {
      callback(query.data.value)
    }
    else {
      const unwatch = watch(isPatching, () => {
        if (!isPatching.value) {
          callback(query.data.value)
          unwatch()
        }
      })
    }
  }

  // Update cache for request list endpoints:
  // TODO: move/merge with query cache update
  watch(query.data, (newVal) => {
    if (!newVal)
      return

    const updateRequestInArray = (requests: WrappedRequest[]) => {
      if (!Array.isArray(requests))
        return requests
      return requests.map((req: WrappedRequest) => {
        if (req.valuation_request_ref === newVal.valuation_request_ref) {
          return newVal
        }
        return req
      })
    }

    const updateIndexCache = function (requestData: any) {
      if (!requestData)
        return requestData

      // Handle infinite query data structure (from useInfiniteQuery)
      // Each page is { data: RequestData[], pagination: PaginationHeader | null }
      if ('pages' in requestData && Array.isArray(requestData.pages)) {
        return {
          ...requestData,
          pages: requestData.pages.map((page: { data: WrappedRequest[] }) => ({
            ...page,
            data: updateRequestInArray(page.data),
          })),
        }
      }

      // Handle regular array (from useQuery)
      if (Array.isArray(requestData)) {
        return updateRequestInArray(requestData)
      }

      // Unknown data structure, return as-is
      return requestData
    }

    // TODO: move to a js file and add a test 😉
    const isRequestQueryKey = function (queryItem: { queryKey: readonly unknown[] }) {
      // Return true iff the queryKey matches [v2, valuation, request, <optional object>] or [v2, valuation, user, request, <optional object>]:
      const [api, version, endpoint, ...pathSegments] = queryItem.queryKey
      if (api !== ReApi.VALUATION || version != ReApiVersion.CURRENT)
        return false
      if (endpoint === ReEndpoint.REQUEST) {
        return pathSegments.length === 0 || typeof pathSegments[0] === 'object'
      }
      if (endpoint === ReEndpoint.USER && pathSegments.length > 0 && pathSegments[0] === 'request') {
        return pathSegments.length === 1 || typeof pathSegments[1] === 'object'
      }
      return false
    }

    queryClient.setQueriesData({ type: 'active', predicate: isRequestQueryKey }, updateIndexCache)
  })

  return reactive({
    requestRef,
    queryKey,
    getRequestRef: reactive(computed(() => requestRef)),
    mutation,
    isBusy: reactiveAny(query.isFetching, query.isLoading, mutation.isPending, requestMutation.isPending),
    ...query,

    isLoaded: computed(() => {
      return query.data.value !== null && query.data.value !== undefined
    }),
    onLoad,
    onPatch,
    updateStatus,
    updateRequestedType,
    updateBorrower,
    updateValuer,
    updateOwner,
    patch,
    patchPricing,
    isPatching,
    createRequest,
    updateRequestRef,

    recomputeAvm,
    isRecomputingAvm,

    // TODO: do we need to expose these?
    // getFeatures: computed(() => query.data?.value?.features),
    getLevel: computed(() => query.data?.value?.features?.level),
    // Disable influence control, as it is not currently in use
    showInfluenceControl: computed(() => false),

    getReactive,
    mapReactives,
    // TODO: enable when we switch to Vue3 and can get reactivity working
    // $r: reactives,
    mapReadOnly,
    mapReactiveNumbers,
    initValues,
    is,
    can,
  })
}
